// src/pages/draft_document/DocumentPreview.jsx
// ✅ DraftDocumentNew.jsx 리팩터링 1단계 — 오른쪽 패널(미리보기) 분리
// values/formData 객체 구성 + ExpenseProposalPreview/IncomeProposalPreview 렌더링을
// 그대로 옮긴 것으로, 로직은 한 글자도 바뀌지 않았다 (순수 이동).
import React from "react";
import styles from "./DraftDocumentNew.module.css";
import IncomeProposalPreview from "../../components/preview/income/IncomeProposalPreview";
import ExpenseProposalPreview from "../../components/preview/expense/ExpenseProposalPreview";
import { EXPENSE_TEMPLATES } from "../../components/preview/expense/expenseTemplates";
import {
  GASAN_MONTH_TEMPLATES,
  formatYearMonth,
  formatPayDate,
  formatDateDots,
  calcVatTotal,
  calcPurchaseRow,
  calcDailyWageRow,
  calcSocialInsuranceRow,
} from "./DraftDocumentNew";

export default function DocumentPreview({
  documentType,
  detailOption,
  category,
  subcategory,
  templateState,
  templateInputs,
  title,
  amount,
  vendorInput,
  serviceContent,
  workMonth,
  executionMonth,
  payMethodLabel,
  claimDate,
  desc,
  attachments,
  aircondDetails,
  tableType,
  draftNo,
  approvers,
  onSave,
  saving,
  onYongyeokViewChange,
}) {
  return (
    <div className={styles.rightPane}>
      {documentType.startsWith("expense") ? (
        <ExpenseProposalPreview
          templateCode={detailOption}
          documentNumber={draftNo}
          approvalLine={approvers}
          onSave={onSave}
          saving={saving}
          values={
            detailOption === "credit_card"
              ? {
                  cardNo: "5794, 8024",
                  month: templateState.creditCardMonth || "2026년 5월",
                  title: title,
                  amount: amount ? Number(amount) : 0,
                  vendor: vendorInput,
                  detail: {
                    rows: [[
                      "결제대금",
                      amount ? Number(amount).toLocaleString() : "-",
                      "-",
                      amount ? Number(amount).toLocaleString() : "-",
                      "매월 23일 이체",
                    ]],
                  },
                }
              : {
                  title: title,
                  category: category,
                  amount: amount ? Number(amount) : 0,
                  vendor:
                    detailOption === "service_commission_health" || detailOption === "service_commission_safety"
                      ? templateState.commissionVendor
                      : vendorInput,
                  item: serviceContent,
                  cardNo: templateState.gasanCardNo,
                  branch: templateState.parkingBranch,
                  fullBranch: vendorInput,
                  reason:
                    detailOption === "donation"
                      ? templateState.donationReason
                      : detailOption === "corporate_seal"
                      ? templateState.sealReason
                      : templateState.parkingReason,
                  month:
                    detailOption === "office_maintenance"
                      ? templateState.maintMonth
                      : detailOption === "electric_bill"
                      ? templateState.elecMonth
                      : detailOption === "pension"
                      ? templateState.pensionMonth
                      : detailOption === "water_rental"
                      ? templateState.waterMonth
                      : detailOption === "service_commission_health" || detailOption === "service_commission_safety"
                      ? templateState.commissionMonth
                      : detailOption === "service_daily_wage"
                      ? templateState.dailyWageMonth
                      : GASAN_MONTH_TEMPLATES.includes(detailOption)
                      ? templateState.gasanMonth
                      : detailOption === "utility_fee"
                      ? templateState.utilityMonth || "2026년 5월"
                      : detailOption === "payroll"
                      ? formatYearMonth(templateState.payDateRaw)
                      : detailOption === "accounting_fee"
                      ? templateState.accountingMonth || "2026년 5월"
                      : executionMonth || "2026년 5월",
                  payDate: formatPayDate(templateState.payDateRaw),
                  method: EXPENSE_TEMPLATES[detailOption]?.defaultMethod || payMethodLabel,
                  target:
                    detailOption === "donation"
                      ? templateState.donationTarget
                      : detailOption === "job_training"
                      ? templateState.trainTarget
                      : detailOption === "service_purchase"
                      ? templateState.purchaseTarget
                      : detailOption === "deduction_refund"
                      ? templateState.refundRows.length <= 1
                        ? templateState.refundRows[0]?.name || ""
                        : `${templateState.refundRows[0]?.name || ""} 외 ${templateState.refundRows.length - 1}명`
                      : templateState.bizTarget,
                  applicant: templateState.welfareApplicant,
                  applyReason: templateState.welfareReason,
                  contractName: templateState.contractName,
                  contractAmount: templateState.contractAmountRaw ? Number(templateState.contractAmountRaw) : 0,
                  period:
                    templateState.contractStartRaw && templateState.contractEndRaw
                      ? `${formatDateDots(templateState.contractStartRaw)}~ ${formatDateDots(templateState.contractEndRaw)}`
                      : "",
                  cost: amount ? `인지세 ${Number(amount).toLocaleString()}원` : "",
                  trainee: templateState.trainee,
                  course: templateState.trainCourse,
                  schedule: templateState.trainSchedule,
                  institution: templateState.trainInstitution,
                  adTarget: templateState.adTarget,
                  carNumber: templateState.carNumber,
                  carModel: templateState.carModel,
                  bizCategory: templateState.bizCategory,
                  year:
                    detailOption === "doksan_vat" || detailOption === "geumnarae_vat" || detailOption === "green_vat"
                      ? templateState.vatYear
                      : (detailOption === "doksan_hygiene_edu" || detailOption === "green_hygiene_edu")
                      ? templateState.hygieneYear
                      : undefined,
                  quarter:
                    detailOption === "doksan_vat" || detailOption === "geumnarae_vat" || detailOption === "green_vat"
                      ? templateState.vatQuarter
                      : undefined,
                  vatType:
                    detailOption === "geumnarae_vat" || detailOption === "green_vat"
                      ? templateState.vatType
                      : undefined,
                  usagePeriod:
                    ["doksan_water", "doksan_gas", "doksan_electric", "geumnarae_water", "geumnarae_gas", "geumnarae_electric", "green_water", "green_gas", "green_electric"].includes(detailOption)
                      ? templateState.utilityUsageStartRaw && templateState.utilityUsageEndRaw
                        ? `${formatDateDots(templateState.utilityUsageStartRaw)} ~ ${formatDateDots(templateState.utilityUsageEndRaw)}`
                        : ""
                      : templateState.elecUsageStartRaw && templateState.elecUsageEndRaw
                      ? `${formatDateDots(templateState.elecUsageStartRaw)} ~ ${formatDateDots(templateState.elecUsageEndRaw)}`
                      : "",
                  dueDate:
                    detailOption === "office_maintenance" || detailOption === "gasan_maintenance" || detailOption === "green_maintenance"
                      ? formatDateDots(templateState.maintDueDateRaw)
                      : detailOption === "electric_bill"
                      ? formatDateDots(templateState.elecDueDateRaw)
                      : detailOption === "pension"
                      ? formatPayDate(templateState.pensionDueRaw)
                      : (detailOption === "withholding_tax" || detailOption === "doksan_withholding_tax" || detailOption === "geumnarae_withholding_tax" || detailOption === "green_withholding_tax")
                      ? formatPayDate(templateState.withholdingDueRaw)
                      : (detailOption === "doksan_vat" || detailOption === "geumnarae_vat" || detailOption === "green_vat")
                      ? formatDateDots(templateState.vatDueRaw)
                      : (detailOption === "doksan_hygiene_edu" || detailOption === "green_hygiene_edu")
                      ? formatDateDots(templateState.hygieneDueRaw)
                      : ["doksan_water", "doksan_gas", "doksan_electric", "geumnarae_water", "geumnarae_gas", "geumnarae_electric", "green_water", "green_gas", "green_electric"].includes(detailOption)
                      ? formatDateDots(templateState.utilityDueRaw)
                      // ✅ 인터넷·전화요금 — 구 페이지 원본 PDF 표기(2026-07-31)와 동일하게 대시 형식
                      // 그대로 사용(다른 템플릿의 formatDateDots 점 표기와 다름, 의도된 차이).
                      : detailOption === "utility_fee"
                      ? templateState.utilityFeeDueDateRaw
                      : undefined,
                  attachments:
                    detailOption === "office_maintenance"
                      ? [
                          { name: `${templateState.maintMonth || ""}분 관리비 명세서`, count: 1 },
                          { name: "관리비 부과 내역서", count: 1 },
                          { name: "계산서", count: 1 },
                          { name: "세금계산서", count: 1 },
                        ]
                      : detailOption === "electric_bill"
                      ? [{ name: `${templateState.elecMonth || ""}분 전기요금 청구서`, count: 1 }]
                      : (detailOption === "doksan_water" || detailOption === "geumnarae_water" || detailOption === "green_water")
                      ? [
                          { name: "수도요금 청구서", count: 1 },
                          { name: "수도요금 정산내역서", count: 1 },
                        ]
                      : (detailOption === "doksan_gas" || detailOption === "geumnarae_gas" || detailOption === "green_gas")
                      ? [
                          { name: "가스요금 청구서", count: 1 },
                          { name: "가스요금 정산내역서", count: 1 },
                        ]
                      : (detailOption === "doksan_electric" || detailOption === "geumnarae_electric" || detailOption === "green_electric")
                      ? [{ name: `${templateState.gasanMonth || ""}분 전기요금 청구서`, count: 1 }]
                      : detailOption === "supplies"
                      ? templateState.supplyPayType === "transfer"
                        ? [
                            { name: "거래명세표", count: 1 },
                            { name: "이체확인증", count: 1 },
                            { name: "현금영수증", count: 1 },
                          ]
                        : [
                            { name: "거래명세표", count: 1 },
                            { name: "카드매출전표", count: 1 },
                          ]
                      : detailOption === "service_daily_wage"
                      ? [
                          {
                            name: `일용근로자 근로내용 확인 신고용 (${
                              (templateState.dailyWageMonth.match(/(\d+)월/) || [])[1] || ""
                            }월) 자료`,
                            count: 1,
                          },
                        ]
                      : undefined,
                  detail:
                    detailOption === "interview_fee"
                      ? {
                          rows: [
                            [
                              "서류심사",
                              `${(Number(templateState.docFee) || 0).toLocaleString()}원*${templateState.docCount || 0}회*${templateState.docPersons || 0}인`,
                              ((Number(templateState.docFee) || 0) * (Number(templateState.docCount) || 0) * (Number(templateState.docPersons) || 0)).toLocaleString(),
                              "-",
                              ((Number(templateState.docFee) || 0) * (Number(templateState.docCount) || 0) * (Number(templateState.docPersons) || 0)).toLocaleString(),
                            ],
                            [
                              "면접심사",
                              `${(Number(templateState.intFee) || 0).toLocaleString()}원*${templateState.intCount || 0}회*${templateState.intPersons || 0}인`,
                              ((Number(templateState.intFee) || 0) * (Number(templateState.intCount) || 0) * (Number(templateState.intPersons) || 0)).toLocaleString(),
                              "-",
                              ((Number(templateState.intFee) || 0) * (Number(templateState.intCount) || 0) * (Number(templateState.intPersons) || 0)).toLocaleString(),
                            ],
                          ],
                        }
                      : detailOption === "deduction_refund"
                      ? {
                          rows: templateState.refundRows.map((row, i) =>
                            i === 0
                              ? [
                                  row.name || "",
                                  (Number(row.amount) || 0).toLocaleString(),
                                  { value: templateState.refundNote || "", rowSpan: templateState.refundRows.length },
                                ]
                              : [row.name || "", (Number(row.amount) || 0).toLocaleString()]
                          ),
                        }
                      : detailOption === "pension"
                      ? {
                          rows: (() => {
                            const totalRowCount = templateState.pensionRows.length + 1; // 부문 행 + 사업자수수료 행
                            const total =
                              templateState.pensionRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0) +
                              (Number(templateState.pensionFeeAmount) || 0);
                            const deptRows = templateState.pensionRows.map((r, i) => {
                              const extra = Number(r.extra) || 0;
                              const nameDisplay = extra > 0 ? `${r.name || ""} 외 ${extra}명` : r.name || "";
                              const cells = [r.dept || "", nameDisplay, (Number(r.amount) || 0).toLocaleString()];
                              if (i === 0) {
                                cells.push({
                                  value: total.toLocaleString(),
                                  rowSpan: totalRowCount,
                                  align: "center",
                                });
                              }
                              return cells;
                            });
                            return [
                              ...deptRows,
                              [
                                { value: "사업자수수료", colSpan: 2 },
                                (Number(templateState.pensionFeeAmount) || 0).toLocaleString(),
                              ],
                            ];
                          })(),
                        }
                      : (detailOption === "withholding_tax" || detailOption === "doksan_withholding_tax" || detailOption === "geumnarae_withholding_tax" || detailOption === "green_withholding_tax")
                      ? {
                          rows: (() => {
                            const dataRows = templateState.withholdingRows.map((r) => {
                              const extra = Number(r.extra) || 0;
                              const nameDisplay = extra > 0 ? `${r.name || ""} 외 ${extra}명` : r.name || "";
                              const labor = Number(r.laborTax) || 0;
                              const local = Number(r.localTax) || 0;
                              return [
                                r.desc || "",
                                nameDisplay,
                                labor.toLocaleString(),
                                local.toLocaleString(),
                                (labor + local).toLocaleString(),
                              ];
                            });
                            const laborSum = templateState.withholdingRows.reduce((sum, r) => sum + (Number(r.laborTax) || 0), 0);
                            const localSum = templateState.withholdingRows.reduce((sum, r) => sum + (Number(r.localTax) || 0), 0);
                            return [
                              ...dataRows,
                              [
                                { value: "합계", colSpan: 2, bold: true },
                                { value: laborSum.toLocaleString(), bold: true },
                                { value: localSum.toLocaleString(), bold: true },
                                { value: (laborSum + localSum).toLocaleString(), bold: true },
                              ],
                            ];
                          })(),
                        }
                      : (detailOption === "doksan_holiday_gift" || detailOption === "geumnarae_holiday_gift" || detailOption === "green_holiday_gift")
                      ? {
                          rows: (() => {
                            const dataRows = templateState.holidayGiftRows.map((r) => {
                              const supply = Number(r.supply) || 0;
                              const vat = Number(r.vat) || 0;
                              return [
                                r.target || "",
                                r.name || "",
                                String(Number(r.qty) || 0),
                                supply.toLocaleString(),
                                vat.toLocaleString(),
                                (supply + vat).toLocaleString(),
                              ];
                            });
                            const supplySum = templateState.holidayGiftRows.reduce((sum, r) => sum + (Number(r.supply) || 0), 0);
                            const vatSum = templateState.holidayGiftRows.reduce((sum, r) => sum + (Number(r.vat) || 0), 0);
                            return [
                              ...dataRows,
                              [
                                { value: "합계", colSpan: 3, bold: true },
                                { value: supplySum.toLocaleString(), bold: true },
                                { value: vatSum.toLocaleString(), bold: true },
                                { value: (supplySum + vatSum).toLocaleString(), bold: true },
                              ],
                            ];
                          })(),
                        }
                      : (detailOption === "doksan_vending_terminal" || detailOption === "green_vending_terminal")
                      ? {
                          rows: (() => {
                            const qty = Number(templateState.vendingQty) || 0;
                            const price = Number(templateState.vendingUnitPrice) || 0;
                            const total = qty * price;
                            const vat = Math.round(total / 11);
                            const supply = total - vat;
                            return [
                              [
                                "자판기용 단말기(음료/커피)이용료",
                                String(qty),
                                price.toLocaleString(),
                                supply.toLocaleString(),
                                vat.toLocaleString(),
                                total.toLocaleString(),
                              ],
                            ];
                          })(),
                        }
                      : detailOption === "water_rental"
                      ? {
                          rows: (() => {
                            const { amt, vat, total } = calcVatTotal(templateState.waterSupply);
                            return [
                              ["정수기 렌탈료", amt.toLocaleString(), vat.toLocaleString(), total.toLocaleString(), templateState.waterNote || ""],
                            ];
                          })(),
                        }
                      : detailOption === "server_fee"
                      ? {
                          rows: (() => {
                            const { amt, vat, total } = calcVatTotal(templateState.serverSupply);
                            return [
                              ["홈페이지 서버이용료 (호스팅 외)", amt.toLocaleString(), vat.toLocaleString(), total.toLocaleString(), templateState.serverNote || ""],
                            ];
                          })(),
                        }
                      : (detailOption === "gasan_pos_rental" || detailOption === "doksan_pos_rental" || detailOption === "geumnarae_pos_rental" || detailOption === "green_pos_rental")
                      ? {
                          rows: (() => {
                            const { amt, vat, total } = calcVatTotal(templateState.posSupply);
                            return [
                              ["포스 임대료", amt.toLocaleString(), vat.toLocaleString(), total.toLocaleString(), templateState.posNote || ""],
                            ];
                          })(),
                        }
                      : (detailOption === "gasan_credit_card" || detailOption === "doksan_credit_card" || detailOption === "geumnarae_credit_card" || detailOption === "green_credit_card")
                      ? {
                          rows: (() => {
                            const fee = Number(templateState.gasanCardFee) || 0;
                            const amt = Number(amount) || 0;
                            const total = amt + fee;
                            const monthNum = (templateState.gasanMonth.match(/(\d+)월/) || [])[1] || "";
                            return [
                              [
                                `${monthNum}월 결제대금`,
                                amt.toLocaleString(),
                                fee.toLocaleString(),
                                total.toLocaleString(),
                                templateState.gasanCardNote || "",
                              ],
                            ];
                          })(),
                        }
                      : (detailOption === "gasan_social_insurance" || detailOption === "doksan_social_insurance" || detailOption === "geumnarae_social_insurance" || detailOption === "green_social_insurance")
                      ? {
                          rows: (() => {
                            const monthNum = (templateState.gasanMonth.match(/(\d+)월/) || [])[1] || "";
                            const employees = templateState.socialInsuranceRows;
                            const totalRowCount = employees.length * 2;
                            const dataRows = [];
                            employees.forEach((r, i) => {
                              const c = calcSocialInsuranceRow(r);
                              const selfRow = [
                                r.name || "",
                                "본인부담금",
                                c.health_self.toLocaleString(),
                                c.care_self.toLocaleString(),
                                c.pension_self.toLocaleString(),
                                c.employment_self.toLocaleString(),
                                "-",
                                c.selfTotal.toLocaleString(),
                              ];
                              if (i === 0) {
                                selfRow.unshift({ value: `${monthNum}월 사회보험료`, rowSpan: totalRowCount });
                              }
                              dataRows.push(selfRow);
                              dataRows.push([
                                r.name || "",
                                "기관부담금",
                                c.health_org.toLocaleString(),
                                c.care_org.toLocaleString(),
                                c.pension_org.toLocaleString(),
                                c.employment_org.toLocaleString(),
                                c.industrial_org.toLocaleString(),
                                c.orgTotal.toLocaleString(),
                              ]);
                            });
                            const sums = employees.reduce(
                              (acc, r) => {
                                const c = calcSocialInsuranceRow(r);
                                acc.health += c.health_self + c.health_org;
                                acc.care += c.care_self + c.care_org;
                                acc.pension += c.pension_self + c.pension_org;
                                acc.employment += c.employment_self + c.employment_org;
                                acc.industrial += c.industrial_org;
                                acc.total += c.selfTotal + c.orgTotal;
                                return acc;
                              },
                              { health: 0, care: 0, pension: 0, employment: 0, industrial: 0, total: 0 }
                            );
                            return [
                              ...dataRows,
                              [
                                { value: "합계", colSpan: 3, bold: true },
                                { value: sums.health.toLocaleString(), bold: true },
                                { value: sums.care.toLocaleString(), bold: true },
                                { value: sums.pension.toLocaleString(), bold: true },
                                { value: sums.employment.toLocaleString(), bold: true },
                                { value: sums.industrial.toLocaleString(), bold: true },
                                { value: sums.total.toLocaleString(), bold: true },
                              ],
                            ];
                          })(),
                        }
                      : detailOption === "service_purchase"
                      ? {
                          rows: (() => {
                            const dataRows = templateState.purchaseRows.map((r) => {
                              const { qty, price, discount, shipping, rowTotal, subtotal } = calcPurchaseRow(r);
                              return [
                                r.name || "",
                                qty.toLocaleString(),
                                price.toLocaleString(),
                                discount.toLocaleString(),
                                rowTotal.toLocaleString(),
                                shipping.toLocaleString(),
                                subtotal.toLocaleString(),
                                r.note || "",
                              ];
                            });
                            const subtotalSum = templateState.purchaseRows.reduce(
                              (sum, r) => sum + calcPurchaseRow(r).subtotal,
                              0
                            );
                            return [
                              ...dataRows,
                              [
                                { value: "합계", colSpan: 6, bold: true },
                                { value: subtotalSum.toLocaleString(), bold: true },
                                "",
                              ],
                            ];
                          })(),
                        }
                      : detailOption === "service_daily_wage"
                      ? {
                          rows: (() => {
                            const dataRows = templateState.dailyWageRows.map((r) => {
                              const { pay, deduction, net } = calcDailyWageRow(r);
                              return [
                                r.name || "",
                                r.site || "",
                                r.workDate || "",
                                pay.toLocaleString(),
                                deduction.toLocaleString(),
                                net.toLocaleString(),
                              ];
                            });
                            const paySum = templateState.dailyWageRows.reduce(
                              (sum, r) => sum + calcDailyWageRow(r).pay,
                              0
                            );
                            const deductionSum = templateState.dailyWageRows.reduce(
                              (sum, r) => sum + calcDailyWageRow(r).deduction,
                              0
                            );
                            const netSum = templateState.dailyWageRows.reduce(
                              (sum, r) => sum + calcDailyWageRow(r).net,
                              0
                            );
                            return [
                              ...dataRows,
                              [
                                { value: "합계", colSpan: 3, bold: true },
                                { value: paySum.toLocaleString(), bold: true },
                                { value: deductionSum.toLocaleString(), bold: true },
                                { value: netSum.toLocaleString(), bold: true },
                              ],
                            ];
                          })(),
                        }
                      : undefined,
                  round: templateState.installmentRound,
                  contractInfo:
                    detailOption === "env_total_car_lease"
                      ? {
                          rows: (() => {
                            const installmentNo = Number(templateState.leaseInstallmentNo) || 0;
                            const remaining = 60 - installmentNo;
                            return [
                              ["취득원가", { value: "31,396,030원", colSpan: 2 }],
                              ["리스기간", { value: "60개월", colSpan: 2 }],
                              ["보증금", "8,904,000원", "30%"],
                              ["월 리스료", { value: "477,300원", colSpan: 2 }],
                              ["수납회차", `${installmentNo}회`, "잔여회차", `${remaining}회`],
                            ];
                          })(),
                        }
                      : {
                          rows: [
                            ["취득원가", { value: "31,304,000원", colSpan: 2 }],
                            ["할부기간", { value: "60개월", colSpan: 2 }],
                            ["계약금", "100,000원", "2025년 1월 10일 납부"],
                            ["월 할부금", { value: "575,808원", colSpan: 2 }],
                          ],
                        },
                  ...templateInputs,
                }
          }
        />
      ) : (
        <IncomeProposalPreview
          onSave={onSave}
          saving={saving}
          onActiveViewChange={onYongyeokViewChange}
          formData={{
            recipient: "내부결재",
            title: title,
            documentType: documentType,
            businessType: category,
            detailType: subcategory,
            detailOption: detailOption,
            workMonth: workMonth,
            startDate: workMonth,
            endDate: workMonth,
            client: vendorInput,
            amount: amount,
            transactionType: {
              계좌이체: payMethodLabel === "계좌이체",
              카드결제: payMethodLabel === "카드결제",
              현금결제: payMethodLabel === "현금결제",
            },
            manualDate: claimDate,
            documentNumber: draftNo,
            content: desc,
            attachments: attachments.filter(att => att.name.trim() !== ""),
            aircondDetails: aircondDetails,
            tableType: tableType,
            // ✅ 용역 수입기안 청구형(공단 계약) 전용
            activeView: templateState.claimActiveView,
            officialDraftNo: templateState.officialDraftNo,
            yongyeokRound: templateState.yongyeokRound,
            yongyeokContractAmount: templateState.yongyeokContractAmount,
            yongyeokPrevAmount: templateState.yongyeokPrevAmount,
            yongyeokPrevCumulative: templateState.yongyeokPrevCumulative,
            yongyeokContractName: templateState.yongyeokContractName,
            yongyeokReferenceLabel: templateState.yongyeokReferenceLabel,
            // ✅ 용역 수입기안 제출형(기성계 제출) 전용
            yongyeokSubmitServiceName: templateState.yongyeokSubmitServiceName,
            yongyeokSubmitRecipient: templateState.yongyeokSubmitRecipient,
            yongyeokSubmitHonorific: templateState.yongyeokSubmitHonorific,
            yongyeokSubmitViaLabel: templateState.yongyeokSubmitViaLabel,
          }}
          approvalLine={approvers}
        />
      )}
    </div>
  );
}
